/**
 * Тарифы Aptile: «Старт» (бесплатно), «Практика» и «Максимум».
 *
 * Ограничено только то, что стоит денег (разговор с Тико: сообщения и
 * минуты звонков) или создаёт привычку (уроки в день, игры в день). Карман,
 * повторение и справочник открыты на всех тарифах: они возвращают ученика.
 * «Максимум» — «без ограничений» с разумным лимитом против злоупотреблений.
 *
 * Калькулятор на экране тарифов собирает бесплатный уровень с опциями до
 * уровня «Практики» по каждому пункту; дороже «Практики» он подсказывает
 * взять её. «Без ограничений» в калькуляторе нет — только у «Максимума».
 *
 * Чистые функции без базы: их зовут сервер (проверка лимитов), клиент
 * (экран тарифов, остаток) и тесты.
 */

import { z } from 'zod';

export const PLANS = ['FREE', 'PRACTICE', 'MAX'] as const;
export type Plan = (typeof PLANS)[number];
export type PaidPlan = Exclude<Plan, 'FREE'>;

export const BILLING_PERIODS = ['month', 'year'] as const;
export type BillingPeriod = (typeof BILLING_PERIODS)[number];

/** Цены, ₽. Год — со скидкой: деньги вперёд и меньше отток. */
export const PLAN_PRICES: Readonly<Record<PaidPlan, Readonly<Record<BillingPeriod, number>>>> = {
  PRACTICE: { month: 390, year: 3290 },
  MAX: { month: 790, year: 6590 },
};

/** Цена года в пересчёте на месяц, ₽, с округлением вниз. */
export function monthlyOfYear(plan: PaidPlan): number {
  return Math.floor(PLAN_PRICES[plan].year / 12);
}

/** Скидка годовой оплаты к помесячной, %, с округлением вниз. */
export function yearDiscountPercent(plan: PaidPlan): number {
  const prices = PLAN_PRICES[plan];
  return Math.floor((1 - prices.year / (prices.month * 12)) * 100);
}

/** Что считается: сообщения чата, минуты звонков, игры в слова, уроки. */
export const METRICS = ['CHAT_MESSAGES', 'CALL_MINUTES', 'WORD_GAMES', 'LESSONS'] as const;
export type Metric = (typeof METRICS)[number];

/** Окно лимита: день, неделя, месяц или всё время (пробный звонок). */
export type LimitWindow = 'day' | 'week' | 'month' | 'lifetime';

export interface Limit {
  readonly amount: number;
  readonly window: LimitWindow;
  /** Разумный лимит «без ограничений»: ученик его не видит, пока не упрётся. */
  readonly fairUse?: boolean;
}

export interface PlanRules {
  /** Сколько первых уроков каждой ступени открыто; `null` — все. */
  readonly lessonsPerLevel: number | null;
  /** Лимиты по метрикам; метрики нет — без ограничений. */
  readonly limits: Readonly<Partial<Record<Metric, Limit>>>;
}

export const PLAN_RULES: Readonly<Record<Plan, PlanRules>> = {
  FREE: {
    lessonsPerLevel: 30,
    limits: {
      LESSONS: { amount: 1, window: 'day' },
      CHAT_MESSAGES: { amount: 10, window: 'week' },
      CALL_MINUTES: { amount: 3, window: 'lifetime' },
      WORD_GAMES: { amount: 1, window: 'day' },
    },
  },
  PRACTICE: {
    lessonsPerLevel: null,
    limits: {
      CHAT_MESSAGES: { amount: 150, window: 'month' },
      CALL_MINUTES: { amount: 60, window: 'month' },
      WORD_GAMES: { amount: 3, window: 'day' },
    },
  },
  MAX: {
    lessonsPerLevel: null,
    limits: {
      CHAT_MESSAGES: { amount: 3000, window: 'month', fairUse: true },
      CALL_MINUTES: { amount: 1000, window: 'month', fairUse: true },
    },
  },
};

/** Код ошибки сервера, когда лимит метрики исчерпан; `LEVEL` — урок дальше открытых на ступени. */
export const LIMIT_CODES: Readonly<Record<Metric | 'LEVEL', string>> = {
  CHAT_MESSAGES: 'LIMIT_REACHED_CHAT',
  CALL_MINUTES: 'LIMIT_REACHED_CALL',
  WORD_GAMES: 'LIMIT_REACHED_GAME',
  LESSONS: 'LIMIT_REACHED_LESSONS',
  LEVEL: 'LIMIT_REACHED_LEVEL',
};

/** Подробности ошибки лимита — модалка пишет по ним человеческий текст. */
export interface LimitReachedDetails {
  readonly metric: Metric | 'LEVEL';
  readonly plan: Plan;
  readonly limit: number;
  readonly window: LimitWindow;
  /** Тариф, на котором этого ограничения нет (или оно шире). */
  readonly upgradeTo: PaidPlan;
}

/** Тариф, который снимает ограничение метрики у текущего тарифа. */
export function upgradeFor(plan: Plan, metric: Metric | 'LEVEL'): PaidPlan {
  if (plan === 'FREE' && (metric === 'LESSONS' || metric === 'LEVEL' || metric === 'WORD_GAMES')) return 'PRACTICE';
  return plan === 'FREE' ? 'PRACTICE' : 'MAX';
}

/** Урок открыт тарифу: `sequenceOrder` — номер урока на ступени, с 1. */
export function lessonAllowed(plan: Plan, sequenceOrder: number): boolean {
  const open = PLAN_RULES[plan].lessonsPerLevel;
  return open === null || sequenceOrder <= open;
}

/** Сколько осталось: `null` — без ограничений. */
export function remaining(plan: Plan, metric: Metric, used: number): number | null {
  const limit = PLAN_RULES[plan].limits[metric];
  return limit === undefined ? null : Math.max(0, limit.amount - used);
}

/**
 * Ключ окна счётчика: день `2026-10-28`, неделя `2026-W44` (ISO), месяц
 * `2026-10`, всё время `all`. Считается по московскому времени: лимит «в
 * день» обнуляется в полночь ученика, а не по UTC.
 */
export function periodKey(window: LimitWindow, date: Date, timeZone = 'Europe/Moscow'): string {
  if (window === 'lifetime') return 'all';
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(date);
  const get = (type: string): number => Number(parts.find((part) => part.type === type)?.value);
  const year = get('year');
  const month = get('month');
  const day = get('day');
  const pad = (value: number): string => String(value).padStart(2, '0');
  if (window === 'month') return `${year}-${pad(month)}`;
  if (window === 'day') return `${year}-${pad(month)}-${pad(day)}`;
  // ISO-неделя: четверг этой недели определяет год, неделя с понедельника.
  const local = new Date(Date.UTC(year, month - 1, day));
  const weekday = (local.getUTCDay() + 6) % 7;
  local.setUTCDate(local.getUTCDate() - weekday + 3);
  const weekYear = local.getUTCFullYear();
  const firstThursday = new Date(Date.UTC(weekYear, 0, 4));
  const week = 1 + Math.round(((local.getTime() - firstThursday.getTime()) / 86_400_000 - 3 + ((firstThursday.getUTCDay() + 6) % 7)) / 7);
  return `${weekYear}-W${pad(week)}`;
}

/* ── Калькулятор ─────────────────────────────────────────────────── */

/** Опции калькулятора: каждая поднимает бесплатный уровень до «Практики» по своему пункту. */
export const CALCULATOR_OPTIONS = ['LESSONS', 'TIKO', 'GAMES'] as const;
export type CalculatorOption = (typeof CALCULATOR_OPTIONS)[number];

export const CALCULATOR_PRICES: Readonly<Record<CalculatorOption, number>> = {
  LESSONS: 190,
  TIKO: 190,
  GAMES: 60,
};

/** Насколько близко к «Практике» сумма, чтобы подсказать «добавь немного». */
export const NUDGE_CLOSE_RUB = 50;

export type CalculatorNudge =
  | { readonly kind: 'none' }
  /** Сумма дороже «Практики»: она выгоднее на `saving` ₽. */
  | { readonly kind: 'cheaper'; readonly plan: 'PRACTICE'; readonly price: number; readonly saving: number }
  /** Сумма чуть меньше «Практики»: за `extra` ₽ больше — весь тариф. */
  | { readonly kind: 'close'; readonly plan: 'PRACTICE'; readonly price: number; readonly extra: number };

export interface CalculatorResult {
  readonly total: number;
  readonly nudge: CalculatorNudge;
}

/** Сумма выбранных опций и подсказка тарифа. */
export function calculate(selected: ReadonlySet<CalculatorOption>): CalculatorResult {
  const total = CALCULATOR_OPTIONS.reduce((sum, option) => sum + (selected.has(option) ? CALCULATOR_PRICES[option] : 0), 0);
  const price = PLAN_PRICES.PRACTICE.month;
  if (total > price) return { total, nudge: { kind: 'cheaper', plan: 'PRACTICE', price, saving: total - price } };
  const extra = price - total;
  if (total > 0 && extra > 0 && extra <= NUDGE_CLOSE_RUB) return { total, nudge: { kind: 'close', plan: 'PRACTICE', price, extra } };
  return { total, nudge: { kind: 'none' } };
}

/* ── Бета: намерение тарифа и представление для клиента ───────────── */

/**
 * «Хочу этот тариф после беты»: оплаты нет, ответ — замер готовности
 * платить. FREE — «останусь на бесплатном», с необязательной причиной.
 */
export const PlanIntentSchema = z.object({
  plan: z.enum(PLANS),
  period: z.enum(BILLING_PERIODS).optional(),
  reason: z.string().trim().max(1000).optional(),
});
export type PlanIntentInput = z.infer<typeof PlanIntentSchema>;

/** Использование метрики в её окне у тарифа ученика. */
export interface UsageView {
  readonly used: number;
  /** `null` — без ограничений. */
  readonly limit: number | null;
  readonly window: LimitWindow;
  readonly fairUse: boolean;
}

/**
 * Реклама за бонус: досмотрел ролик — лимит, в который упёрся, растёт.
 * Каждый вид бонуса поднимает свои метрики:
 *   LESSON — +1 урок к дневному лимиту;
 *   CHAT_MINUTES — +5 к разговору с Тико: и сообщений чата, и минут звонка
 *     (чат меряется сообщениями, звонок — минутами);
 *   GAME_PLAY — +1 игра «Слова с Тико».
 * Бонусы копятся своими счётчиками в `UsageCounter` (`counter`, по одной
 * записи за просмотр), а не уменьшают использование: учёт, по которому
 * выбираются лимиты, остаётся честным. Бонус действует в том же окне, что
 * и лимит (день, неделя, месяц), и не чаще `AD_BONUSES_PER_DAY` раз в день
 * каждого вида — реклама не заменяет тариф.
 */
export const AD_BONUS_TYPES = ['LESSON', 'CHAT_MINUTES', 'GAME_PLAY'] as const;
export type AdBonusType = (typeof AD_BONUS_TYPES)[number];

export interface AdBonusRule {
  /** Счётчик просмотров в `UsageCounter`; не метрика тарифа. */
  readonly counter: string;
  /** Чьи лимиты поднимает бонус. */
  readonly metrics: readonly Metric[];
  /** На сколько единиц метрики — за один просмотр. */
  readonly amount: number;
}

export const AD_BONUSES: Readonly<Record<AdBonusType, AdBonusRule>> = {
  // Имя счётчика уроков — прежнее: уже начисленные бонусы продолжают считаться.
  LESSON: { counter: 'AD_BONUS_LESSONS', metrics: ['LESSONS'], amount: 1 },
  CHAT_MINUTES: { counter: 'AD_BONUS_CHAT', metrics: ['CHAT_MESSAGES', 'CALL_MINUTES'], amount: 5 },
  GAME_PLAY: { counter: 'AD_BONUS_GAMES', metrics: ['WORD_GAMES'], amount: 1 },
};

/** Сколько раз в день можно получить бонус каждого вида. */
export const AD_BONUSES_PER_DAY = 3;

/** Бонус за рекламу, который снимает этот лимит; `null` — рекламой не снять (ступень). */
export function adBonusFor(metric: Metric | 'LEVEL'): AdBonusType | null {
  if (metric === 'LEVEL') return null;
  return AD_BONUS_TYPES.find((type) => AD_BONUSES[type].metrics.includes(metric)) ?? null;
}

export const AdBonusRequestSchema = z.object({
  // Старый клиент шлёт пустое тело — это бонусный урок.
  bonusType: z.enum(AD_BONUS_TYPES).default('LESSON'),
});
export type AdBonusRequest = z.input<typeof AdBonusRequestSchema>;

export interface AdBonusView {
  readonly bonusType: AdBonusType;
  /** На сколько единиц вырос лимит за этот просмотр. */
  readonly amount: number;
  /** Просмотров этого вида сегодня, вместе с этим. */
  readonly bonusToday: number;
  /** Сколько ещё раз сегодня можно получить этот бонус. */
  readonly bonusLeft: number;
  readonly billing: BillingView;
}

export interface BillingView {
  /** Действуют ли купленные тарифы (`BILLING_ENFORCED`); в бете — нет, у всех «Практика». */
  readonly enforced: boolean;
  /** Тариф, по которому сейчас действуют лимиты. */
  readonly plan: Plan;
  readonly period: BillingPeriod | null;
  readonly until: string | null;
  readonly intent: { readonly plan: Plan; readonly period: BillingPeriod | null } | null;
  readonly usage: Readonly<Record<Metric, UsageView>>;
}
